import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import WishlistItemCard from './components/WishlistItemCard';
import WishlistSummary from './components/WishlistSummary';
import WishlistFilters from './components/WishlistFilters';
import BulkActionsPanel from './components/BulkActionsPanel';
import EmptyWishlistState from './components/EmptyWishlistState';
import BudgetSetupModal from './components/BudgetSetupModal';

const Wishlist = () => {
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [budgetLimit, setBudgetLimit] = useState(0);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: 'dateAdded',
    priority: 'all',
    category: 'all',
    priceRange: 'all',
    compatibility: 'all',
    priceAlert: false,
    hasNote: false
  });
  const [loading, setLoading] = useState(true);

  // Mock wishlist data
  const mockWishlistItems = [
    {
      id: 1,
      name: "Shimano Ultegra R8100 Groupset",
      brand: "Shimano",
      category: "drivetrain",
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&h=300&fit=crop",
      currentPrice: 1299.99,
      originalPrice: 1399.99,
      priceChange: -25.50,
      priority: "high",
      compatibility: "perfect",
      dateAdded: "Dec 15, 2024",
      lastUpdated: "2 hours ago",
      priceAlert: true,
      note: "Perfect for my road bike upgrade. Waiting for better price.",
      tags: ["performance", "road", "electronic"],
      vendors: [
        { name: "BikeShop Pro", price: 1299.99, url: "#" },
        { name: "Cycling Central", price: 1349.99, url: "#" },
        { name: "Gear Hub", price: 1389.99, url: "#" }
      ]
    },
    {
      id: 2,
      name: "Continental GP5000 Tires (700x25c)",
      brand: "Continental",
      category: "wheels",
      image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=300&h=300&fit=crop",
      currentPrice: 89.99,
      originalPrice: 89.99,
      priceChange: 0,
      priority: "medium",
      compatibility: "good",
      dateAdded: "Dec 12, 2024",
      lastUpdated: "1 day ago",
      priceAlert: false,
      note: "",
      tags: ["comfort", "durability"],
      vendors: [
        { name: "Tire World", price: 89.99, url: "#" },
        { name: "Cycling Central", price: 94.99, url: "#" }
      ]
    },
    {
      id: 3,
      name: "Fizik Antares R3 Saddle",
      brand: "Fizik",
      category: "saddle",
      image: "https://images.unsplash.com/photo-1544191696-15693072b5a5?w=300&h=300&fit=crop",
      currentPrice: 179.99,
      originalPrice: 199.99,
      priceChange: -15.00,
      priority: "low",
      compatibility: "perfect",
      dateAdded: "Dec 10, 2024",
      lastUpdated: "3 days ago",
      priceAlert: true,
      note: "Recommended by bike fitter. Good reviews for long rides.",
      tags: ["comfort", "ergonomic"],
      vendors: [
        { name: "Comfort Cycling", price: 179.99, url: "#" },
        { name: "BikeShop Pro", price: 189.99, url: "#" },
        { name: "Gear Hub", price: 199.99, url: "#" }
      ]
    },
    {
      id: 4,
      name: "SRAM Force AXS Brake/Shift Levers",
      brand: "SRAM",
      category: "brakes",
      image: "https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=300&h=300&fit=crop",
      currentPrice: 649.99,
      originalPrice: 699.99,
      priceChange: -50.00,
      priority: "high",
      compatibility: "adapter",
      dateAdded: "Dec 8, 2024",
      lastUpdated: "5 days ago",
      priceAlert: true,
      note: "",
      tags: ["wireless", "performance"],
      vendors: [
        { name: "SRAM Direct", price: 649.99, url: "#" },
        { name: "Cycling Central", price: 679.99, url: "#" }
      ]
    },
    {
      id: 5,
      name: "Garmin Edge 1040 Solar",
      brand: "Garmin",
      category: "accessories",
      image: "https://images.unsplash.com/photo-1606890737304-57a1ca8a5b62?w=300&h=300&fit=crop",
      currentPrice: 699.99,
      originalPrice: 749.99,
      priceChange: -49.99,
      priority: "medium",
      compatibility: "perfect",
      dateAdded: "Dec 5, 2024",
      lastUpdated: "1 week ago",
      priceAlert: false,
      note: "Solar charging feature looks amazing for long rides.",
      tags: ["navigation", "solar", "training"],
      vendors: [
        { name: "Garmin Store", price: 699.99, url: "#" },
        { name: "Tech Cycling", price: 729.99, url: "#" }
      ]
    }
  ];

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    // Load wishlist data
    const loadWishlistData = () => {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        const savedWishlist = localStorage.getItem('userWishlist');
        const savedBudget = localStorage.getItem('userBudgetLimit');
        
        if (savedWishlist) {
          const parsedWishlist = JSON.parse(savedWishlist);
          setWishlistItems(parsedWishlist);
        } else {
          setWishlistItems(mockWishlistItems);
          localStorage.setItem('userWishlist', JSON.stringify(mockWishlistItems));
        }
        
        if (savedBudget) {
          setBudgetLimit(parseFloat(savedBudget));
        }
        
        setLoading(false);
      }, 800);
    };

    loadWishlistData();
  }, [navigate]);

  useEffect(() => {
    // Apply filters
    let filtered = [...wishlistItems];

    // Priority filter
    if (filters?.priority !== 'all') {
      filtered = filtered?.filter(item => item?.priority === filters?.priority);
    }

    // Category filter
    if (filters?.category !== 'all') {
      filtered = filtered?.filter(item => item?.category === filters?.category);
    }

    // Price range filter
    if (filters?.priceRange !== 'all') {
      filtered = filtered?.filter(item => {
        const price = item?.currentPrice;
        switch (filters?.priceRange) {
          case 'under50': return price < 50;
          case '50to100': return price >= 50 && price <= 100;
          case '100to250': return price >= 100 && price <= 250;
          case '250to500': return price >= 250 && price <= 500;
          case 'over500': return price > 500;
          default: return true;
        }
      });
    }

    // Compatibility filter
    if (filters?.compatibility !== 'all') {
      filtered = filtered?.filter(item => item?.compatibility === filters?.compatibility);
    }

    // Price alert filter
    if (filters?.priceAlert) {
      filtered = filtered?.filter(item => item?.priceAlert);
    }

    // Has note filter
    if (filters?.hasNote) {
      filtered = filtered?.filter(item => item?.note && item?.note?.trim() !== '');
    }

    // Sort items
    filtered?.sort((a, b) => {
      switch (filters?.sortBy) {
        case 'dateAdded':
          return new Date(b.dateAdded) - new Date(a.dateAdded);
        case 'dateAddedOld':
          return new Date(a.dateAdded) - new Date(b.dateAdded);
        case 'priceHigh':
          return b?.currentPrice - a?.currentPrice;
        case 'priceLow':
          return a?.currentPrice - b?.currentPrice;
        case 'priority':
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          return priorityOrder?.[b?.priority] - priorityOrder?.[a?.priority];
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'priceChange':
          return a?.priceChange - b?.priceChange;
        default:
          return 0;
      }
    });

    setFilteredItems(filtered);
  }, [wishlistItems, filters]);

  const handleRemoveItem = (itemId) => {
    const updatedItems = wishlistItems?.filter(item => item?.id !== itemId);
    setWishlistItems(updatedItems);
    setSelectedItems(selectedItems?.filter(id => id !== itemId));
    localStorage.setItem('userWishlist', JSON.stringify(updatedItems));
  };

  const handlePriorityChange = (itemId, newPriority) => {
    const updatedItems = wishlistItems?.map(item =>
      item?.id === itemId ? { ...item, priority: newPriority } : item
    );
    setWishlistItems(updatedItems);
    localStorage.setItem('userWishlist', JSON.stringify(updatedItems));
  };

  const handleAddNote = (itemId, note) => {
    const updatedItems = wishlistItems?.map(item =>
      item?.id === itemId ? { ...item, note } : item
    );
    setWishlistItems(updatedItems);
    localStorage.setItem('userWishlist', JSON.stringify(updatedItems));
  };

  const handleToggleAlert = (itemId) => {
    const updatedItems = wishlistItems?.map(item =>
      item?.id === itemId ? { ...item, priceAlert: !item?.priceAlert } : item
    );
    setWishlistItems(updatedItems);
    localStorage.setItem('userWishlist', JSON.stringify(updatedItems));
  };

  const handleItemSelection = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems?.filter(id => id !== itemId));
    }
  };

  const handleBulkAction = (action, data = {}) => {
    switch (action) {
      case 'selectAll':
        setSelectedItems(filteredItems?.map(item => item?.id));
        break;
      case 'remove':
        const remainingItems = wishlistItems?.filter(item => !data?.items?.includes(item?.id));
        setWishlistItems(remainingItems);
        setSelectedItems([]);
        localStorage.setItem('userWishlist', JSON.stringify(remainingItems));
        break;
      case 'changePriority':
        const updatedItems = wishlistItems?.map(item =>
          data?.items?.includes(item?.id) ? { ...item, priority: data?.priority } : item
        );
        setWishlistItems(updatedItems);
        localStorage.setItem('userWishlist', JSON.stringify(updatedItems));
        break;
      case 'toggleAlerts':
        const alertUpdatedItems = wishlistItems?.map(item =>
          data?.items?.includes(item?.id) ? { ...item, priceAlert: !item?.priceAlert } : item
        );
        setWishlistItems(alertUpdatedItems);
        localStorage.setItem('userWishlist', JSON.stringify(alertUpdatedItems));
        break;
      case 'export':
        handleExportWishlist(data?.items);
        break;
      case 'share':
        handleShareWishlist(data?.items);
        break;
      default:
        break;
    }
  };

  const handleSetBudget = () => {
    setShowBudgetModal(true);
  };

  const handleSaveBudget = (budgetData) => {
    setBudgetLimit(budgetData?.amount);
    localStorage.setItem('userBudgetLimit', budgetData?.amount?.toString());
    localStorage.setItem('userBudgetSettings', JSON.stringify(budgetData));
  };

  const handleExportWishlist = (itemIds = null) => {
    const itemsToExport = itemIds 
      ? wishlistItems?.filter(item => itemIds?.includes(item?.id))
      : wishlistItems;
    
    const exportData = {
      exportDate: new Date()?.toISOString(),
      totalItems: itemsToExport?.length,
      totalValue: itemsToExport?.reduce((sum, item) => sum + item?.currentPrice, 0),
      items: itemsToExport?.map(item => ({
        name: item?.name,
        brand: item?.brand,
        category: item?.category,
        currentPrice: item?.currentPrice,
        priority: item?.priority,
        compatibility: item?.compatibility,
        note: item?.note,
        vendors: item?.vendors
      }))
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bike-wishlist-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShareWishlist = (itemIds = null) => {
    const itemsToShare = itemIds 
      ? wishlistItems?.filter(item => itemIds?.includes(item?.id))
      : wishlistItems;
    
    const shareText = `Check out my bike upgrade wishlist!\n\n${itemsToShare?.map(item => 
      `• ${item?.name} - $${item?.currentPrice?.toFixed(2)}`
    )?.join('\n')}\n\nTotal value: $${itemsToShare?.reduce((sum, item) => sum + item?.currentPrice, 0)?.toFixed(2)}`;

    if (navigator.share) {
      navigator.share({
        title: 'My Bike Upgrade Wishlist',
        text: shareText,
        url: window.location?.href
      });
    } else {
      navigator.clipboard?.writeText(shareText);
      alert('Wishlist copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                <div className="lg:col-span-3 space-y-4">
                  {[1, 2, 3]?.map(i => (
                    <div key={i} className="h-48 bg-muted rounded-lg"></div>
                  ))}
                </div>
                <div className="space-y-4">
                  {[1, 2]?.map(i => (
                    <div key={i} className="h-32 bg-muted rounded-lg"></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              My Wishlist
            </h1>
            <p className="text-muted-foreground">
              Save and track your desired bike upgrades with price monitoring and purchase planning.
            </p>
          </div>

          {wishlistItems?.length === 0 ? (
            <EmptyWishlistState />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3 space-y-6">
                {/* Filters */}
                <WishlistFilters
                  onFiltersChange={setFilters}
                  totalItems={filteredItems?.length}
                  activeFilters={filters}
                />

                {/* Bulk Actions */}
                <BulkActionsPanel
                  selectedItems={selectedItems}
                  onBulkAction={handleBulkAction}
                  onClearSelection={() => setSelectedItems([])}
                  totalItems={filteredItems?.length}
                />

                {/* Items Grid */}
                {filteredItems?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredItems?.map(item => (
                      <WishlistItemCard
                        key={item?.id}
                        item={item}
                        onRemove={handleRemoveItem}
                        onPriorityChange={handlePriorityChange}
                        onAddNote={handleAddNote}
                        onToggleAlert={handleToggleAlert}
                        isSelected={selectedItems?.includes(item?.id)}
                        onSelect={handleItemSelection}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-muted-foreground mb-4">
                      No items match your current filters.
                    </div>
                    <button
                      onClick={() => setFilters({
                        sortBy: 'dateAdded',
                        priority: 'all',
                        category: 'all',
                        priceRange: 'all',
                        compatibility: 'all',
                        priceAlert: false,
                        hasNote: false
                      })}
                      className="text-accent hover:text-accent/80 transition-colors"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <WishlistSummary
                    items={wishlistItems}
                    budgetLimit={budgetLimit}
                    onSetBudget={handleSetBudget}
                    onExportWishlist={() => handleExportWishlist()}
                    onShareWishlist={() => handleShareWishlist()}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Budget Setup Modal */}
      <BudgetSetupModal
        isOpen={showBudgetModal}
        onClose={() => setShowBudgetModal(false)}
        onSave={handleSaveBudget}
        currentBudget={budgetLimit}
      />
    </div>
  );
};

export default Wishlist;